import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import Header from "../../Components/Header";
import { sendGetRequest, sendPostRequest } from "../../Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "flowbite-react";
import { Notyf } from "notyf";
import "notyf/notyf.min.css";

const HelpersTable = ({
  items,
  title,
  valuesById,
  handleChange,
  doUpdate,
  isUpdatingAny,
}) => (
  <div className="mb-10">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <div className="text-xs text-gray-500">{items.length} helpers</div>
    </div>

    <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
      <table className="w-full text-sm text-left text-gray-500 table-fixed">
        <colgroup>
          <col className="w-1/2" />
          <col className="w-1/2" />
          <col className="w-36" />
        </colgroup>
        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3">
              Helper Name
            </th>
            <th scope="col" className="px-4 py-3">
              Value
            </th>
            <th scope="col" className="px-4 py-3"></th>
          </tr>
        </thead>

        <tbody>
          {items.length === 0 ? (
            <tr>
              <td className="px-4 py-4 text-sm text-gray-500" colSpan={3}>
                No helpers found.
              </td>
            </tr>
          ) : (
            items.map((h) => {
              const currentValue = valuesById[h.helperID] ?? "";

              return (
                <tr key={h.helperID} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm font-medium text-gray-700 whitespace-nowrap">
                    {h.helperParent}
                  </td>

                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={currentValue}
                      onChange={(e) => handleChange(h.helperID, e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      placeholder="Enter helper value…"
                    />
                  </td>

                  <td className="w-36 px-4 py-2 font-medium text-gray-900 whitespace-nowrap">
                    <Button
                      size="sm"
                      disabled={isUpdatingAny}
                      onClick={async () => {
                        try {
                          await doUpdate({
                            helperID: h.helperID,
                            value: currentValue,
                          });
                        } catch (e) {
                          console.log(e);
                        }
                      }}
                    >
                      Update
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default function Helpers() {
  const queryClient = useQueryClient();

  const notyf = useMemo(
    () =>
      new Notyf({
        position: { x: "right", y: "top" },
      }),
    []
  );

  // Load helpers
  const { data, isLoading, isError } = useQuery({
    queryKey: ["helpers"],
    queryFn: () => sendGetRequest("helpers", {}),
  });

  const helpers = data?.helpers ?? [];

  const headlineHelpers = useMemo(
    () => helpers.filter((h) => Number(h.headline) === 1),
    [helpers]
  );

  const otherHelpers = useMemo(
    () => helpers.filter((h) => Number(h.headline) !== 1),
    [helpers]
  );

  // Local editable state for inputs, keyed by helperID
  const [valuesById, setValuesById] = useState({});

  useEffect(() => {
    if (!helpers.length) return;

    setValuesById((prev) => {
      const next = { ...prev };

      for (const h of helpers) {
        const serverVal = h.helperValue ?? "";

        // If we haven't set this input yet, seed it
        if (next[h.helperID] === undefined) {
          next[h.helperID] = serverVal;
        }
      }

      return next;
    });
  }, [helpers]);

  const updateHelper = async ({ helperID, value }) => {
    return sendPostRequest("helper/update", { helperID, value });
  };

  const { mutateAsync: doUpdate, isPending: isUpdatingAny } = useMutation({
    mutationFn: updateHelper,
    // Optimistic update so UI reflects immediately
    onMutate: async ({ helperID, value }) => {
      await queryClient.cancelQueries({ queryKey: ["helpers"] });

      const previous = queryClient.getQueryData(["helpers"]);

      queryClient.setQueryData(["helpers"], (old) => {
        if (!old?.helpers) return old;
        return {
          ...old,
          helpers: old.helpers.map((h) =>
            h.helperID === helperID ? { ...h, value } : h
          ),
        };
      });

      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["helpers"], ctx.previous);
      notyf.error("Could not save helper");
      console.log(err);
    },
    onSuccess: () => {
      notyf.success("Helper updated");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["helpers"] });
    },
  });

  const handleChange = (helperID, nextValue) => {
    setValuesById((prev) => ({ ...prev, [helperID]: nextValue }));
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
          <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
            <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg p-6">
              <div className="text-sm text-gray-500">Loading helpers…</div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Header />
        <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
          <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
            <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg p-6">
              <div className="text-sm text-red-600">
                Failed to load helpers.
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Header />

      <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
        <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
          <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg">
            {/* Header bar (same vibe as Providers) */}
            <div className="border-b border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between w-[94%] ml-[3%] mt-[3%] pb-6">
              <h3 className="text-base font-semibold text-gray-900">Helpers</h3>
            </div>

            <div className="overflow-x-auto p-6">
              <div className="p-6">
                <HelpersTable
                  title="Headline helpers"
                  items={headlineHelpers}
                  valuesById={valuesById}
                  handleChange={handleChange}
                  doUpdate={doUpdate}
                  isUpdatingAny={isUpdatingAny}
                />

                <HelpersTable
                  title="Strategy helpers"
                  items={otherHelpers}
                  valuesById={valuesById}
                  handleChange={handleChange}
                  doUpdate={doUpdate}
                  isUpdatingAny={isUpdatingAny}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
